import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import { ContractStatus } from "@shared-types/contract-status";
import { TruncatePipe } from "@shared-pipes/truncate.pipe";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { SubscriptionOverviewService } from '../../../home/data-access/subscription-overview.service';
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { WorkflowBasic } from "@shared-interfaces/shared-workflow";
import { parseActiveStepPropToChartValues } from "@shared-helper/helper-functions";
import { ContractNameDialogComponent } from "../../../contract/ui/contract-name-dialog/contract-name-dialog.component";
import { DeleteContractDialogComponent } from "./delete-contract-dialog/delete-contract-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'contract-tile',
    standalone: true,
    imports: [
        TruncatePipe,
        MatMenuModule,
        MatIconModule,
        DatePipe,
        TranslateModule
    ],
    templateUrl: './contract-tile.component.html',
    styleUrl: './contract-tile.component.scss'
})
export class ContractTileComponent implements AfterViewInit, OnDestroy {

    @Input() status: ContractStatus = ContractStatus.YES;

    public parsed!: any;
    private _contract!: WorkflowBasic;
    @Input() set contract(contract: WorkflowBasic) {
        this._contract = contract;
        this.parsed = parseActiveStepPropToChartValues(contract.activeStep.toString(), this.translateService)
        this.barChartData.datasets = [
            {
                label: this.translateService.instant('No'),
                data: [this.parsed.no],
                backgroundColor: '#f44336'
            },
            {
                label: this.translateService.instant('ToBeChecked'),
                data: [this.parsed.check],
                backgroundColor: '#ED8B00'
            },
            {
                label: this.translateService.instant('Yes'),
                data: [this.parsed.yes],
                backgroundColor: '#86BC25'
            }
        ]
    }

    public get contract(): WorkflowBasic {
        return this._contract;
    }

    subscriptionOverview = inject(SubscriptionOverviewService);
    chart: Chart | any;
    barChartData = {
        labels: ['Status'],
        datasets: [
            {
                label: this.translateService.instant('Yes'),
                data: [40],
                backgroundColor: '#f44336'
            },
            {
                label: this.translateService.instant('ToBeChecked'),
                data: [30],
                backgroundColor: '#ED8B00'
            },
            {
                label: this.translateService.instant('No'),
                data: [30],
                backgroundColor: '#86BC25'
            }
        ]
    };
    chartId: string;

    constructor(private router: Router,
        private subscriptionService: SubscriptionOverviewService,
        private dialog: MatDialog, private translateService: TranslateService) {
        this.chartId = `chart-${Math.random().toString(36).substr(2, 9)}`;
    }

    ngAfterViewInit(): void {
        Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend);
        const ctx = document.getElementById(this.chartId) as HTMLCanvasElement;
        if (ctx) {
            this.chart = new Chart(ctx, {
                type: 'bar',
                data: this.barChartData,
                options: {
                    indexAxis: 'y',
                    maintainAspectRatio: false,
                    responsive: true,
                    scales: {
                        x: {
                            stacked: true,
                            display: false
                        },
                        y: {
                            stacked: true,
                            display: false
                        }
                    },
                    plugins: {
                        tooltip: {
                            enabled: false
                        },

                    },
                    layout: {
                        padding: {
                            top: 5,
                            bottom: 5,
                            left: 0,
                            right: 0
                        }
                    }
                },
                plugins: [
                    {
                        id: 'customVerticalLegend', // Custom plugin for vertical legend below the chart
                        afterLayout: (chart) => {
                            if (!chart.legend || !chart.legend.legendItems) {
                                return;
                            }

                            const legendDiv = document.getElementById(`${this.chartId}-legend`);
                            if (!legendDiv) {
                                return;
                            }
                            legendDiv.innerHTML = '';

                            chart.legend.legendItems.forEach((item, index) => {
                                const legendItemDiv = document.createElement('div');
                                legendItemDiv.style.display = 'flex';
                                legendItemDiv.style.alignItems = 'start';
                                legendItemDiv.style.marginBottom = '4px';
                                legendItemDiv.style.justifyContent = 'center';

                                const colorBox = document.createElement('span');
                                colorBox.style.backgroundColor = typeof item.fillStyle === 'string' ? item.fillStyle : '#000';
                                colorBox.style.width = '10px';
                                colorBox.style.height = '10px';
                                colorBox.style.display = 'inline-block';
                                colorBox.style.marginRight = '8px';

                                const dataValue = chart.data.datasets[index].data[0];

                                const labelText = document.createElement('span');
                                labelText.textContent = `${item.text} (${dataValue})`;
                                labelText.style.fontSize = '10px';

                                legendItemDiv.appendChild(colorBox);
                                legendItemDiv.appendChild(labelText);
                                legendDiv.appendChild(legendItemDiv);
                            });
                        }
                    }
                ]
            });
        }
    }

    ngOnDestroy(): void {
        if (this.chart) {
            this.chart.destroy();
        }
    }

    public openContract(uid: string): void {
        // TODO: Improve, use location strategy for preserving subscription id
        this.router.navigate(['/contract/', uid], { queryParams: { subscription_id: this.subscriptionService.subscriptionId() } });
    }

    public editName(): void {
        let dialogRef = this.dialog.open(ContractNameDialogComponent, {
            height: "fit-content",
            width: "max(200px, 400px)",
            data: { name: this.contract.name },
        });

        dialogRef.afterClosed().subscribe((workflowName: string) => {
            if (workflowName) {
                this.subscriptionService.updateWorflowName(this.contract.uid, workflowName).subscribe();
            }
        });
    }

    public deleteWorkflow(workflowId: string): void {
        const dialogRef = this.dialog.open(DeleteContractDialogComponent, {
            width: 'max(200px, 400px)',
        });

        dialogRef.afterClosed().subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.subscriptionOverview.deleteWorkflowId$.next(workflowId);
            }
        });
    }
}
